import { SampleCategories } from 'api';
import { TextField, useFormContext, useWatch } from 'form';
import { useEffect, useMemo } from 'react';
import { InputAdornment, Stack, Typography } from 'ui';

interface FieldItem {
  name: SampleCategories;
  label: string;
}

interface FieldGroup {
  group?: string;
  items: FieldItem[];
}

export const ManualBreakdownFormFields = ({ fields }: { fields: FieldGroup[] }) => {
  const { control, setValue } = useFormContext();
  const watchedValues = useWatch({ control, name: 'breakdown' });
  // Memoize the breakdownValues to ensure stability in the dependency array.
  const breakdownValues = useMemo(() => watchedValues || [], [watchedValues]);

  // Initialize the form with all required fields and handle new fields
  useEffect(() => {
    // Get all field names from the configuration
    const configFieldNames = new Set(fields.flatMap((group) => group.items.map((item) => item.name)));

    // If the breakdown array is empty, initialize all fields
    if (breakdownValues.length === 0) {
      const allFields = fields.flatMap((group) =>
        group.items.map((item) => ({
          name: item.name,
          unit: 'kilogram',
          value: '',
        }))
      );

      // Set the entire breakdown array at once
      setValue('breakdown', allFields);
      return;
    }

    // Check if there are any new fields that need to be added
    const existingFieldNames = new Set(breakdownValues.map((item: { name: SampleCategories }) => item.name));

    // Find field names that exist in the config but not in the current breakdown
    const newFieldNames = Array.from(configFieldNames).filter((name) => !existingFieldNames.has(name));

    // If there are new fields, add them to the breakdown
    if (newFieldNames.length > 0) {
      // Create entries for the new fields
      const newFields = newFieldNames.map((name) => ({
        name,
        unit: 'kilogram',
        value: '',
      }));

      // Combine existing values with new fields
      const updatedBreakdown = [...breakdownValues, ...newFields];

      // Set the updated breakdown array
      setValue('breakdown', updatedBreakdown);
    }
  }, [fields, setValue, breakdownValues]);

  return (
    <Stack gap={2}>
      <Typography variant="body1" fontWeight="bold">
        Breakdown
      </Typography>
      {fields.map((group, groupIndex) => (
        <Stack key={`group-${groupIndex}`} gap={2}>
          {group.group ? (
            <Typography variant="body2" fontWeight="bold">
              {group.group}
            </Typography>
          ) : null}
          {group.items.map((field) => {
            // Find the index of this field in the watched breakdown array.
            const index = breakdownValues.findIndex((item?: { name: string }) => item?.name === field.name);
            if (index < 0) return null; // Should not happen since we set all items.
            return (
              <TextField
                key={field.name}
                name={`breakdown.${index}.value`}
                label={field.label}
                type="number"
                rules={{
                  min: 0,
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Kg</InputAdornment>,
                }}
              />
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
};
